import {
  deleteBusinessEntityLocation,
  TUpdateLocationParams,
  updateBusinessEntityLocation,
} from '@commenda/api';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Icons,
  LoadingIndicator,
  useToggle,
} from '@commenda/kakashi';
import { ILocation, LocationTypeRevamp } from '@commenda/types';
import { useNotification } from '../../../../../hooks/useNotification';

interface DeleteLocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  businessEntityId: number;
  onSuccess: () => void;
  selectedLocation: ILocation & { locationTypesArray: LocationTypeRevamp[] };
}

const DeleteLocationModal = ({
  isOpen,
  onClose,
  businessEntityId,
  selectedLocation,
  onSuccess,
}: DeleteLocationModalProps) => {
  const isDeleting = useToggle();
  const { setNotification } = useNotification();

  const handleDeleteLocation = async () => {
    isDeleting.on();

    try {
      // we need to create a new location for the location type and update the existing location by removing the location type from the array
      if (selectedLocation?.locationTypesArray.length > 1) {
        const updatedLoactionTypes =
          selectedLocation?.locationTypesArray.filter(
            (locationType) =>
              locationType !== selectedLocation?.locationTypes.toString()
          );

        const updateBody = {
          locationTypes: updatedLoactionTypes,
          address: {
            addressLine1: selectedLocation?.address?.addressLine1,
            addressLine2: selectedLocation?.address?.addressLine2,
            addressLine3: selectedLocation?.address?.addressLine3,
            city: selectedLocation?.address?.city,
            postalCode: selectedLocation?.address?.postalCode,
            state: selectedLocation?.address?.state,
            country: selectedLocation?.address?.country,
          },
        } as TUpdateLocationParams;

        const updatedLocation = await updateBusinessEntityLocation(
          businessEntityId,
          selectedLocation.id,
          updateBody
        );

        if (!updatedLocation.ok) {
          setNotification({
            title: 'Error',
            message: 'Failed to update existing location',
            show: true,
            valid: false,
          });
          return;
        }
      } else {
        const response = await deleteBusinessEntityLocation(
          businessEntityId,
          selectedLocation.id
        );

        if (!response.ok) {
          setNotification({
            title: 'Error',
            message: 'Failed to delete location.',
            valid: false,
            show: true,
          });
          return;
        }
      }

      await onSuccess();

      setNotification({
        title: 'Location removed!',
        message: 'This location can be added again at any time.',
        valid: true,
        show: true,
      });

      onClose();
    } catch (error) {
      setNotification({
        title: 'Error',
        message: 'Failed to delete location.',
        valid: false,
        show: true,
      });
    } finally {
      isDeleting.off();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader className="border-b-0">
          <div className="size-12 rounded-lg bg-bg-error flex justify-center items-center">
            <Icons.Trash01 className="size-6 text-utility-error-500" />
          </div>
        </DialogHeader>

        <div className="flex flex-col gap-xs px-6">
          <div className="txt-large-bold text-text-primary">
            Delete this location
          </div>
          <p className="txt-small-regular text-text-tertiary">
            This action cannot be undone. Are you sure you want to delete this
            business presence?
          </p>
        </div>

        <DialogFooter className="border-t-0">
          <Button variant="secondary" size="md" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="destructive-primary"
            size="md"
            onClick={handleDeleteLocation}
            disabled={isDeleting.value}
          >
            {isDeleting.value && <LoadingIndicator size={16} />}
            <span>Confirm & Delete</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { DeleteLocationModal };
